import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_URL from "../config";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [editEndDate, setEditEndDate] = useState("");
  const [showEndDateModal, setShowEndDateModal] = useState(false);
  const [endDateAppId, setEndDateAppId] = useState(null);

  // Protect admin route
  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isAdminLoggedIn");
    if (!isLoggedIn) {
      navigate("/admin/login");
    }
  }, [navigate]);

  // Fetch students
  const fetchStudents = () => {
    setLoading(true);
    fetch(`${API_URL}/students`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.students)) {
          setStudents(data.students);
        } else {
          setStudents([]);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  // Delete
  const handleDelete = async (certId) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      const res = await fetch(`${API_URL}/delete/${certId}`, { method: "DELETE" });
      const result = await res.json();
      if (result.success) {
        fetchStudents();
        setShowProfile(false);
      } else {
        alert("Failed to delete");
      }
    } catch {
      alert("Server error");
    }
  };

  // Approve
  const handleApprove = async (appId) => {
    if (!window.confirm("Approve this application and auto-generate certificate?")) return;
    try {
      const res = await fetch(`${API_URL}/approve/${appId}`, { method: "POST" });
      const result = await res.json();
      if (result.success) {
        alert(`✅ Approved! Cert ID: ${result.cert_id}\n${result.email_sent ? "📧 Email sent to student." : "⚠️ Email not sent (configure SMTP in Settings)."}`);
        fetchStudents();
        setShowProfile(false);
      } else {
        alert(result.error || "Failed to approve");
      }
    } catch {
      alert("Server error");
    }
  };

  // Update end date
  const handleUpdateEndDate = async () => {
    if (!endDateAppId || !editEndDate) return;
    try {
      const res = await fetch(`${API_URL}/update-end-date/${endDateAppId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ internship_to: editEndDate }),
      });
      const result = await res.json();
      if (result.success) {
        alert("✅ End date updated successfully!");
        setShowEndDateModal(false);
        fetchStudents();
        setShowProfile(false);
      } else {
        alert(result.error || "Failed to update");
      }
    } catch {
      alert("Server error");
    }
  };

  // Send email
  const handleSendEmail = async (certId) => {
    try {
      const res = await fetch(`${API_URL}/send-cert-email/${certId}`, { method: "POST" });
      const result = await res.json();
      alert(result.message);
    } catch {
      alert("Server error");
    }
  };

  // Filter
  const filtered = students.filter((s) => {
    const matchSearch =
      `${s.student_name} ${s.email} ${s.cert_id} ${s.college}`
        .toLowerCase()
        .includes(search.toLowerCase());
    if (activeTab === "all") return matchSearch;
    if (activeTab === "pending") return matchSearch && s.status === "pending";
    if (activeTab === "approved") return matchSearch && s.status === "approved";
    return matchSearch;
  });

  // View profile
  const viewProfile = (student) => {
    setSelectedStudent(student);
    setShowProfile(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Student Management</h1>
            <p className="text-sm text-slate-500">
              Manage applications, approve internships, and issue certificates
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/admin/settings")}
              className="px-4 py-2.5 border border-slate-300 text-slate-600 rounded-xl hover:bg-slate-100 transition text-sm font-medium"
            >
              ⚙️ Settings
            </button>
            <button
              onClick={() => navigate("/admin/add")}
              className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-sm font-medium shadow-sm"
            >
              + Add Certificate
            </button>
          </div>
        </div>

        {/* Search & Tabs */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <input
                type="text"
                placeholder="🔍 Search by name, email, ID, college..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full sm:w-96 px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-sm"
              />
              <div className="flex gap-2">
                {["all", "pending", "approved"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                      activeTab === tab
                        ? "bg-indigo-100 text-indigo-700"
                        : "text-slate-500 hover:bg-slate-100"
                    }`}
                  >
                    {tab === "all" ? "All" : tab === "pending" ? "⏳ Pending" : "✅ Approved"}
                    {tab === "all" && (
                      <span className="ml-1.5 px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-xs">
                        {students.length}
                      </span>
                    )}
                    {tab === "pending" && (
                      <span className="ml-1.5 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                        {students.filter((s) => s.status === "pending").length}
                      </span>
                    )}
                    {tab === "approved" && (
                      <span className="ml-1.5 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                        {students.filter((s) => s.status === "approved").length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Student Cards - Horizontal Scroll */}
          {loading ? (
            <div className="py-20 text-center text-slate-500">
              <div className="inline-block w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4" />
              <p>Loading students...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center text-slate-500">
              <p className="text-5xl mb-4">📭</p>
              <p className="font-medium">No students found</p>
              <p className="text-sm mt-1">
                {search ? "Try a different search term" : "No applications yet"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="flex gap-4 p-4 min-w-max">
                {filtered.map((student) => (
                  <div
                    key={student.cert_id}
                    onClick={() => viewProfile(student)}
                    className="flex-shrink-0 w-72 bg-white border border-slate-200 rounded-xl p-5 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                  >
                    {/* Photo */}
                    <div className="flex justify-center mb-4">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center overflow-hidden ring-2 ring-indigo-200 group-hover:ring-indigo-400 transition-all">
                        {student.student_photo_url ? (
                          <img
                            src={student.student_photo_url}
                            alt={student.student_name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.parentElement.innerHTML = '<span class="text-3xl">👤</span>';
                            }}
                          />
                        ) : (
                          <span className="text-3xl">👤</span>
                        )}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="text-center">
                      <h3 className="font-semibold text-slate-800 truncate">
                        {student.student_name}
                      </h3>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {student.college}
                      </p>
                      <p className="text-xs text-slate-400 font-mono mt-1">
                        {student.cert_id}
                      </p>
                    </div>

                    {/* Status Badge */}
                    <div className="mt-3 text-center">
                      {student.status === "pending" ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                          ⏳ Pending
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                          ✅ Approved
                        </span>
                      )}
                    </div>

                    {/* Quick Actions */}
                    <div className="mt-3 flex justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {student.status === "pending" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApprove(student.cert_id);
                          }}
                          className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition"
                        >
                          Approve
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/edit/${student.cert_id}`);
                        }}
                        className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-xs font-medium hover:bg-indigo-200 transition"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Student Profile Modal */}
        {showProfile && selectedStudent && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowProfile(false)}>
            <div
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Profile Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-8 text-white relative">
                <button
                  onClick={() => setShowProfile(false)}
                  className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition"
                >
                  ✕
                </button>
                <div className="flex items-center gap-6">
                  <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center overflow-hidden ring-4 ring-white/30">
                    {selectedStudent.student_photo_url ? (
                      <img
                        src={selectedStudent.student_photo_url}
                        alt={selectedStudent.student_name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML = '<span class="text-4xl">👤</span>';
                        }}
                      />
                    ) : (
                      <span className="text-4xl">👤</span>
                    )}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{selectedStudent.student_name}</h2>
                    <p className="text-indigo-200 text-sm">{selectedStudent.college}</p>
                    <p className="text-indigo-200 text-xs font-mono mt-1">{selectedStudent.cert_id}</p>
                  </div>
                </div>
                <div className="mt-4">
                  {selectedStudent.status === "pending" ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-yellow-200 text-yellow-900">
                      ⏳ Pending Approval
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-green-200 text-green-900">
                      ✅ Approved
                    </span>
                  )}
                </div>
              </div>

              {/* Profile Details */}
              <div className="p-8 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Email</label>
                    <p className="text-slate-800 font-medium">{selectedStudent.email}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Phone</label>
                    <p className="text-slate-800 font-medium">{selectedStudent.phone}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Department</label>
                    <p className="text-slate-800 font-medium">{selectedStudent.department}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Date of Birth</label>
                    <p className="text-slate-800 font-medium">{selectedStudent.dob}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Age</label>
                    <p className="text-slate-800 font-medium">{selectedStudent.age}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider">College</label>
                    <p className="text-slate-800 font-medium">{selectedStudent.college}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Address</label>
                    <p className="text-slate-800 font-medium">{selectedStudent.address}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Internship From</label>
                    <p className="text-slate-800 font-medium">{selectedStudent.internship_from}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Internship To</label>
                    <p className="text-slate-800 font-medium">
                      {selectedStudent.internship_to}
                      {selectedStudent.status === "pending" && (
                        <button
                          onClick={() => {
                            setEndDateAppId(selectedStudent.cert_id);
                            setEditEndDate(selectedStudent.internship_to || "");
                            setShowEndDateModal(true);
                          }}
                          className="ml-2 text-xs text-indigo-600 hover:underline"
                        >
                          (Edit)
                        </button>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider">Applied On</label>
                    <p className="text-slate-800 font-medium">{selectedStudent.created_at}</p>
                  </div>
                </div>

                {/* Certificate Photo */}
                {selectedStudent.cert_photo_url && (
                  <div>
                    <label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">
                      Generated Certificate
                    </label>
                    <img
                      src={selectedStudent.cert_photo_url}
                      alt="Certificate"
                      className="w-full rounded-xl border border-slate-200 shadow-sm"
                    />
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-100">
                  {selectedStudent.status === "pending" && (
                    <>
                      <button
                        onClick={() => handleApprove(selectedStudent.cert_id)}
                        className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium hover:from-green-600 hover:to-emerald-700 transition shadow-sm"
                      >
                        ✅ Approve & Generate Certificate
                      </button>
                      <button
                        onClick={() => {
                          setEndDateAppId(selectedStudent.cert_id);
                          setEditEndDate(selectedStudent.internship_to || "");
                          setShowEndDateModal(true);
                        }}
                        className="px-6 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-medium hover:bg-slate-100 transition"
                      >
                        📅 Set End Date
                      </button>
                    </>
                  )}
                  {selectedStudent.status === "approved" && (
                    <button
                      onClick={() => handleSendEmail(selectedStudent.cert_id)}
                      className="px-6 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-medium hover:from-indigo-600 hover:to-purple-700 transition shadow-sm"
                    >
                      📧 Resend Certificate Email
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/admin/edit/${selectedStudent.cert_id}`)}
                    className="px-6 py-2.5 border border-indigo-300 text-indigo-600 rounded-xl font-medium hover:bg-indigo-50 transition"
                  >
                    ✏️ Edit Details
                  </button>
                  <button
                    onClick={() => handleDelete(selectedStudent.cert_id)}
                    className="px-6 py-2.5 border border-red-200 text-red-600 rounded-xl font-medium hover:bg-red-50 transition"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* End Date Modal */}
        {showEndDateModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEndDateModal(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Set Internship End Date</h3>
              <p className="text-sm text-slate-500 mb-4">
                Update the internship end date. The certificate will be generated with this date upon approval.
              </p>
              <input
                type="date"
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:outline-none mb-4"
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowEndDateModal(false)}
                  className="px-4 py-2 border border-slate-300 rounded-xl text-slate-600 hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateEndDate}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}